/**
 * Box Component
 * A versatile container component that supports all common styling props
 */

import React from "react";
import { Box as ChakraBox } from "@chakra-ui/react";
import { useColorModeValue } from "design-system/hooks/useColorModeValue";
import { resolveSpacing, resolveSize } from "design-system/foundation";
import type { CommonStyleProps } from "design-system/types/CommonProps";

export interface BoxProps extends CommonStyleProps {
  children?: React.ReactNode;
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  role?: string;
  "aria-label"?: string;
  "data-testid"?: string;
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

function BoxComponent({
  children,
  as,
  className,
  style,
  id,
  role,
  "aria-label": ariaLabel,
  "data-testid": testId,
  onClick,
  onMouseEnter,
  onMouseLeave,
  // Spacing props
  p,
  px,
  py,
  pt,
  pr,
  pb,
  pl,
  m,
  mx,
  my,
  mt,
  mr,
  mb,
  ml,
  // Size props
  w,
  h,
  minW,
  minH,
  maxW,
  maxH,
  // Color props
  bg,
  backgroundColor,
  borderColor,
  // Layout props
  borderRadius,
  borderWidth,
  ...props
}: BoxProps) {
  // Resolve colors using design system hooks
  const resolvedBg = bg ? useColorModeValue(bg) : backgroundColor ? useColorModeValue(backgroundColor) : undefined;
  const resolvedBorderColor = borderColor ? useColorModeValue(borderColor) : undefined;

  // Resolve spacing and sizing
  const resolvedPadding = p ? resolveSpacing(p) : undefined;
  const resolvedPaddingX = px ? resolveSpacing(px) : undefined;
  const resolvedPaddingY = py ? resolveSpacing(py) : undefined;
  const resolvedPaddingTop = pt ? resolveSpacing(pt) : undefined;
  const resolvedPaddingRight = pr ? resolveSpacing(pr) : undefined;
  const resolvedPaddingBottom = pb ? resolveSpacing(pb) : undefined;
  const resolvedPaddingLeft = pl ? resolveSpacing(pl) : undefined;

  const resolvedMargin = m ? resolveSpacing(m) : undefined;
  const resolvedMarginX = mx ? resolveSpacing(mx) : undefined;
  const resolvedMarginY = my ? resolveSpacing(my) : undefined;
  const resolvedMarginTop = mt ? resolveSpacing(mt) : undefined;
  const resolvedMarginRight = mr ? resolveSpacing(mr) : undefined;
  const resolvedMarginBottom = mb ? resolveSpacing(mb) : undefined;
  const resolvedMarginLeft = ml ? resolveSpacing(ml) : undefined;

  const resolvedWidth = w ? resolveSize(w) : undefined;
  const resolvedHeight = h ? resolveSize(h) : undefined;
  const resolvedMinWidth = minW ? resolveSize(minW) : undefined;
  const resolvedMinHeight = minH ? resolveSize(minH) : undefined;
  const resolvedMaxWidth = maxW ? resolveSize(maxW) : undefined;
  const resolvedMaxHeight = maxH ? resolveSize(maxH) : undefined;

  const resolvedBorderRadius = borderRadius ? resolveSpacing(borderRadius) : undefined;
  const resolvedBorderWidth = borderWidth ? resolveSpacing(borderWidth) : undefined;

  return (
    <ChakraBox
      as={as}
      className={className}
      style={style}
      id={id}
      role={role}
      aria-label={ariaLabel}
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // Resolved spacing
      p={resolvedPadding}
      px={resolvedPaddingX}
      py={resolvedPaddingY}
      pt={resolvedPaddingTop}
      pr={resolvedPaddingRight}
      pb={resolvedPaddingBottom}
      pl={resolvedPaddingLeft}
      m={resolvedMargin}
      mx={resolvedMarginX}
      my={resolvedMarginY}
      mt={resolvedMarginTop}
      mr={resolvedMarginRight}
      mb={resolvedMarginBottom}
      ml={resolvedMarginLeft}
      // Resolved sizing
      w={resolvedWidth}
      h={resolvedHeight}
      minW={resolvedMinWidth}
      minH={resolvedMinHeight}
      maxW={resolvedMaxWidth}
      maxH={resolvedMaxHeight}
      // Resolved colors
      bg={resolvedBg}
      borderColor={resolvedBorderColor}
      // Resolved layout
      borderRadius={resolvedBorderRadius}
      borderWidth={resolvedBorderWidth}
      {...props}
    >
      {children}
    </ChakraBox>
  );
}

// Public API - just the component
export const Box = BoxComponent;
